/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { css, cssMap, jsx } from '@atlaskit/css';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { useMotion } from '@atlaskit/motion/entering/use-motion';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { MessageWrapperContext } from './message-context';
import MessageTrack from './message-track';

type MessageAppearance = 'default' | 'error' | 'valid';

/**
 * API for the internal `<Message />` component. This is not public API.
 */
interface InternalMessageProps {
	/**
	 * The content of the message
	 */
	children: ReactNode;
	/**
	 * A testId prop is provided for specified elements, which is a unique string
	 *  that appears as a data attribute data-testid in the rendered code,
	 *  serving as a hook for automated tests
	 */
	testId?: string;
	/**
	 * Determines the appearance of the message.
	 */
	appearance?: MessageAppearance;
	fieldId?: string;
}
/**
 * Public API of the various message components.
 */
export type MessageProps = Pick<InternalMessageProps, 'children' | 'testId'>;

const messageStyles = css({
	display: 'flex',
	justifyContent: 'baseline',
	gap: token('space.075'),
	font: token('font.body.small'),
	marginBlockStart: token('space.050'),
});

const messageAppearanceStyles = cssMap({
	default: {
		color: token('color.text.subtlest'),
	},
	error: {
		color: token('color.text.danger'),
	},
	valid: {
		color: token('color.text.success'),
	},
});

const messageMotionStyles = cssMap({
	hidden: {
		visibility: 'hidden',
	},
	entering: {
		animation: token('motion.form.message.enter'),
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	exiting: {
		animation: token('motion.form.message.exit'),
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
});

const iconWrapperStyles = cssMap({
	root: {
		display: 'flex',
		height: '16px',
		alignItems: 'center',
	},
});

const IconWrapper = ({ children }: { children: ReactNode }) => (
	<span css={iconWrapperStyles.root}>{children}</span>
);

const messageIcons: Partial<Record<MessageAppearance, JSX.Element>> = {
	error: <ErrorIcon color="currentColor" label="error" size="small" />,
	valid: <SuccessIcon color="currentColor" label="success" size="small" />,
};

type MessageMotionState = ReturnType<typeof useMotion>['state'];

interface MessageRowProps extends InternalMessageProps {
	motionRef?: (node: HTMLDivElement | null) => void;
	motionState?: MessageMotionState;
	shouldAnimate?: boolean;
}

const MessageRow = ({
	children,
	appearance = 'default',
	fieldId,
	motionRef,
	motionState,
	shouldAnimate = false,
	testId,
}: MessageRowProps): React.ReactNode => {
	const icon = messageIcons[appearance];
	const messageRef = useRef<HTMLDivElement>(null);
	const ref = useMemo(
		() => (motionRef ? mergeRefs([messageRef, motionRef]) : messageRef),
		[motionRef],
	);
	const [hasMessageWrapper, setHasMessageWrapper] = useState(false);
	const { isWrapper } = useContext(MessageWrapperContext);

	useEffect(() => {
		if (messageRef.current) {
			setHasMessageWrapper(isWrapper);
		}
	}, [isWrapper]);

	/**
	 * The wrapping span is necessary to preserve spaces between children.
	 * Otherwise the flex layout of the message will remove any whitespace
	 * between children.
	 *
	 * If the child is just a string, this is not required and we can use one
	 * less DOM element.
	 */
	const content = typeof children === 'string' ? children : <span>{children}</span>;

	return (
		<div
			css={[
				messageStyles,
				messageAppearanceStyles[appearance],
				shouldAnimate &&
					(motionState === 'init' || motionState === 'hidden') &&
					messageMotionStyles.hidden,
				shouldAnimate && motionState === 'entering' && messageMotionStyles.entering,
				shouldAnimate && motionState === 'exiting' && messageMotionStyles.exiting,
			]}
			data-testid={testId}
			id={motionState === 'exiting' ? undefined : fieldId}
			ref={ref}
			// For backwards compatability, if there is a wrapper, aria-live is not needed
			aria-live={!hasMessageWrapper ? 'polite' : undefined}
			aria-hidden={motionState === 'exiting' ? true : undefined}
		>
			{icon && <IconWrapper>{icon}</IconWrapper>}
			{content}
		</div>
	);
};

const MotionMessage = (props: InternalMessageProps): React.ReactNode => {
	const isErrorMessage = props.appearance === 'error';
	const { hasMotionBoundary, transition } = useContext(MessageWrapperContext);
	const shouldAnimate = isErrorMessage || hasMotionBoundary;
	const { ref, state } = useMotion<HTMLDivElement>({
		initialState: shouldAnimate && transition !== 'instant' ? undefined : 'visible',
	});
	const row = (
		<MessageRow
			appearance={props.appearance}
			fieldId={props.fieldId}
			motionRef={hasMotionBoundary ? undefined : ref}
			motionState={state}
			shouldAnimate={isErrorMessage && !hasMotionBoundary}
			testId={props.testId}
		>
			{props.children}
		</MessageRow>
	);

	if (!hasMotionBoundary) {
		return row;
	}

	return (
		<MessageTrack
			state={state}
			transition={transition}
			motionRef={ref}
			testId={props.testId && `${props.testId}-motion`}
		>
			{row}
		</MessageTrack>
	);
};

/**
 * __Message__
 *
 * A message component for displaying messages in a form.
 */
const Message = (props: InternalMessageProps): React.ReactNode => {
	if (!fg('platform-dst-motion-uplift-input')) {
		return (
			<MessageRow appearance={props.appearance} fieldId={props.fieldId} testId={props.testId}>
				{props.children}
			</MessageRow>
		);
	}

	return (
		<MotionMessage appearance={props.appearance} fieldId={props.fieldId} testId={props.testId}>
			{props.children}
		</MotionMessage>
	);
};

export default Message;
